<!-- eslint-disable unused-imports/no-unused-vars -->
<script lang="ts" setup>
import type { VCombobox, VTextField } from 'vuetify/components'
import { CacheGroup, StorageGetCache, StorageListCache } from '@d/cache'
import { VERSIONING_FOLDER } from '@d/cache/versioning'
import { StorageNodeTypeEnum } from '@d/sdk'
import { PathGuide } from '@d/util'
import { mdiChevronRight, mdiContentCopy, mdiExclamation, mdiFile, mdiFolder, mdiFolderRemove, mdiPlus, mdiProgressQuestion, mdiRedo, mdiServer, mdiRename, mdiFilePlus, mdiFileMove } from '@mdi/js'
import { useArrayScope } from './helper/array'

type SelectType = 'file' | 'folder'
type Action = 'move' | 'copy' | 'select'

interface Props {
  action?: Action
  label?: string
  modelValue?: string[]
  original?: string[]
  checkDuplicate?: boolean
  rules?: Array<((value: string[]) => string | boolean) | ((value: string[]) => Promise<string | boolean>)>
  exclude?: Array<string[]>
  selectType?: SelectType
  showExtra?: boolean
  allowNewFolders?: boolean
  allowSamePath?: boolean
  initialParent?: boolean
  showFiles?: boolean
  hideDetails?: boolean
  minPathLen?: number
  allowTextView?: boolean
  hideMetaFiles?: boolean
  noAdd?: boolean
  mode?: 'add' | 'copy' | 'move'
}
const props = withDefaults(defineProps<Props>(), {
  exclude: undefined,
  selectType: 'folder' as SelectType,
  showExtra: true,
  action: 'select',
  allowTextView: true,
  hideDetails: false,
  hideMetaFiles: true,
  mode: 'add',
})

const emit = defineEmits(['update:modelValue', 'update:cache'])

const { t } = useI18n({
  useScope: 'local',
})

const cacheGroup = new CacheGroup(onBeforeUnmount)

const search = ref<string>()
const hasFocus = ref<boolean>(false)
const textView = ref(false)
const textViewRef = ref<InstanceType<typeof VTextField>>()
const menuRef = ref(false)
const comboboxRef = ref<InstanceType<typeof VCombobox>>()
const inputRef = computed(() => comboboxRef.value?.$el.querySelector('input'))

whenever(() => !menuRef.value && hasFocus.value, () => menuRef.value = true)

const modelValue = useVModel(props, 'modelValue', emit)

const originalFileOrFolder = computed<string | null>(() => {
  if (props.original) {
    return props.original[props.original.length - 1]
  }
  return null
})

const originalExtension = computed<string | null>(() => {
  if (!originalFileOrFolder.value)
    return null
  const file = originalFileOrFolder.value
  const lastDot = file.lastIndexOf('.')
  if (lastDot === -1) {
    return null
  }
  return file.slice(lastDot + 1)
})

const loading = ref(false)
const _newPath = ref<Array<string>>(modelValue.value ?? [])

// If initialParent is true, move the initial path one level up (pop last segment)
// This should only run once on initialization so we track it with a flag.
const _initialParentApplied = ref(false)
watch(modelValue, (v) => {
  if (_initialParentApplied.value || !props.initialParent)
    return

  const arr = (v ?? []).map((e: any) => typeof e === 'string' ? e : (e?.value ?? e?.title ?? '')).filter((x: string) => x)
  if (arr.length === 0) {
    _initialParentApplied.value = true
    return
  }

  // Pop the last segment to get the parent. If there's only one segment this results in [] (root)
  _newPath.value = arr.slice(0, -1)
  _initialParentApplied.value = true
}, { immediate: true })

const fullPath = computed(() => {
  if (!modelValue.value)
    return '/'

  return `/${modelValue.value.map((x: string | { value: string, title: string }) => {
    if (typeof x === 'string') {
      return x
    }
    return x.title
  }).join('/')}`
  // const isStrArr = typeof (newPath.value![newPath.value!.length - 1]) === "string"

  // if(isStrArr) {
  //   return '/' + newPath.value!.join('/')
  // }

  // return (newPath.value![newPath.value!.length - 1] as any)?.value ?? '/'
})

const fullPathInternal = computed(() => {
  if (!_newPath.value)
    return '/'
  return `/${_newPath.value.join('/')}`
})

/**
 * Check if the current path is a file or a folder
 * If path is original path, return true if selectType is 'file'
 * If path in cache is a folder, return false
 * If path in cache is a file, return true
 * If path doesn't exist in cache, if the name ends with an extension, return true
 * Otherwise, return false
 */
async function _pathIsFileFn(v: string[]): Promise<boolean> {
  if (v.join('/') === props.original?.join('/')) {
    return props.selectType !== 'folder'
  }

  if (props.selectType === 'folder') {
    return false
  }

  const node = StorageGetCache.use(v.join('/'), cacheGroup)

  try {
    const n = await node.valueAsync
    return n?.type === StorageNodeTypeEnum.File
  }
  catch {
    return false
  }
}

const pathIsFileFn = useDebounceFn(_pathIsFileFn, 400)

// const pathExists = asyncComputed(async () => {
//   const node = StorageGetCache.use(fullPathInternal.value, cacheGroup)

//   try {
//     const n = await node.valueAsync
//     return !!n
//   }
//   catch {
//     return false
//   }
// }, false, { evaluating: loading })
// const pathIsFile = asyncComputed(() => textView.value ? false : pathIsFileFn(_newPath.value), false, { evaluating: loading })

// Return true if the path is a confirmed file (file actually exists)

const fullPathInternalDebounced = refDebounced(fullPathInternal, 400)

const pathIsConfirmedFile = asyncComputed(async () => {
  const node = StorageGetCache.use(fullPathInternalDebounced.value, cacheGroup)

  try {
    const n = await node.valueAsync
    return n?.type === StorageNodeTypeEnum.File
  }
  catch {
    return false
  }
}, false, { evaluating: loading })

const allPaths = computed(() => {
  let prepend = ''
  const path: string[] = []
  modelValue.value?.forEach((v, i) => {
    path.push((i === 0 ? '/' : '') + prepend + v)
    prepend += `${i === 0 ? '/' : ''}${v}/`
  })
  if (props.original && !path.includes(`/${props.original.join('/')}`)) {
    path.push(`/${props.original.join('/')}`)
  }
  return path
})

const allPathsDebounced = refDebounced(allPaths, 400)

const pathMeta = useArrayScope(allPathsDebounced, async (val) => {
  const node = StorageGetCache.use(val, cacheGroup)
  const retObj = reactive({ exists: false, folder: false, loading: true })

  try {
    const element = await node.valueAsync
    retObj.exists = !!element
    retObj.folder = element?.type !== StorageNodeTypeEnum.File
  }
  finally {
    retObj.loading = false
  }

  return retObj
})

onUnmounted(() => {
  pathMeta.clear()
})

const originalIsFolder = computed(() => {
  if (!props.original)
    return false
  const meta = pathMeta.get(`/${props.original.join('/')}`)
  console.log('Got original meta', props.original, meta)
  return !!meta?.folder
})

const displayItemsExtra = computed(() => {
  if (!props.showExtra)
    return []
  if (pathIsConfirmedFile.value) {
    return [{ title: '', value: '', type: 'addInsideFile' }]
  }

  const prepend: Array<{ title: string, value: string, type?: string, pathType?: StorageNodeTypeEnum }> = []

  if (search.value && !props.noAdd) {
    let fileName = search.value
    // If the original path is a folder, don't suggest the original extension.
    const pg = PathGuide.from(props.original ?? [])
    console.log(pg, pg.extension)
    if (pg.extension.toLowerCase() === 'manual') {
      fileName += '.manual'
    }
    else if (!fileName.includes('.') && originalExtension.value && (!originalIsFolder.value)) {
      fileName += `.${originalExtension.value}`
    }

    prepend.push({ title: fileName, value: fileName, type: 'addFile', pathType: undefined })
  }

  return prepend
})

function arraysEqual(a?: string[] | null, b?: string[] | null): boolean {
  if (!a || !b)
    return false
  if (a.length !== b.length)
    return false
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i])
      return false
  }
  return true
}

const addItemKey = computed(() => {
  const mode = (props.action as string | undefined) ?? props.mode
  const originalParent = props.original ? props.original.slice(0, -1) : null
  const targetParent = _newPath.value

  if (mode === 'move' && originalParent && arraysEqual(originalParent, targetParent)) {
    return 'renameFile'
  }
  if (mode === 'copy')
    return 'copyFile'
  if (mode === 'move')
    return 'moveFile'
  return 'addFile'
})

const addItemIcon = computed(() => {
  const key = addItemKey.value
  switch (key) {
    case 'renameFile':
      return mdiRename
    case 'copyFile':
      return mdiContentCopy
    case 'moveFile':
      return mdiFileMove
    case 'addFile':
    default:
      return mdiFilePlus
  }
})

const displayItems = asyncComputed(async () => {
  const searchValue = search.value ?? ''

  function removeLastSlash(str: string): string {
    return str.endsWith('/') ? str.slice(0, -1) : str
  }

  if (pathIsConfirmedFile.value) {
    return displayItemsExtra.value
  }

  const curSearch = removeLastSlash(fullPathInternalDebounced.value)

  try {
    const files = await StorageListCache.use(curSearch, cacheGroup).valueAsync
    const directories: Array<{ value: string, title: string, pathType?: StorageNodeTypeEnum }> = files!
      .filter(e => props.selectType === 'folder' ? e.type !== StorageNodeTypeEnum.File : true)
      .filter(e => props.showFiles ? true : e.type !== StorageNodeTypeEnum.File)
      .filter(e => !props.hideMetaFiles || e.type !== StorageNodeTypeEnum.Directory || e.name !== VERSIONING_FOLDER) // hide versioning folders
      .filter(e => !props.hideMetaFiles || e.type !== StorageNodeTypeEnum.Bin || e.name !== 'binfolder.bin') // hide trashbin folders
      .map(e => ({ value: `${curSearch}/${e.name}/`, title: e.name, pathType: e.type }))
      .filter(e => e.title.toLowerCase().startsWith(searchValue.toLowerCase()))

    if (searchValue)
      directories.splice(0, 0, ...displayItemsExtra.value)

    if (props.exclude === undefined) {
      return directories
    }
    const excludePaths = props.exclude.map(ex => `/${ex.join('/')}/`)
    return directories.filter(e => !excludePaths.includes(e.value))
  }
  catch {
    return displayItemsExtra.value
  }
}, [], { evaluating: loading })

const __newPath = computed({
  get: () => _newPath.value,
  set: (v) => {
    let p: Array<string> = []
    p = p.flatMap(x => x.split('/')).filter(x => x)

    for (let i = 0; i < v.length; i++) {
      const e = v[i]
      const val = typeof e === 'string' ? e : (e as { title: string }).title
      if (val === '..') {
        p.pop()
        continue
      }
      p.push(val)
    }

    // Disallow nesting the target inside the original path.
    // If the constructed path starts with the original path, remove any entries after the original path.
    if (props.original && p.length > props.original.length) {
      const orig = props.original
      let startsWithOriginal = true
      for (let i = 0; i < orig.length; i++) {
        if (p[i] !== orig[i]) {
          startsWithOriginal = false
          break
        }
      }
      if (startsWithOriginal) {
        p = p.slice(0, orig.length)
      }
    }

    _newPath.value = p
  },
})

let ignoreNextSanityCheck = false

async function _pathSanityCheck(): Promise<void> {
  if (ignoreNextSanityCheck) {
    ignoreNextSanityCheck = false
    return
  }
  else {
    ignoreNextSanityCheck = true
  }
  const cg = new CacheGroup()
  const fixedPath: string[] = []
  for (let i = 0; i < _newPath.value.length; i++) {
    const path = `/${_newPath.value.slice(0, i + 1).join('/')}`
    const node = StorageGetCache.use(path, cg)

    try {
      const n = await node.valueAsync
      fixedPath.push(_newPath.value[i])
      if (n?.type === StorageNodeTypeEnum.File)
        break
    }
    catch {
      fixedPath.push(_newPath.value[i])
      // lastWasDirectory = true
      // cg.clear()
    }
  }

  if (fixedPath.join('/') !== _newPath.value.join('/')) {
    _newPath.value = fixedPath
  }
  cg.clear()
}

const pathSanityCheck = useDebounceFn(_pathSanityCheck, 400)

watch([__newPath, pathIsConfirmedFile], pathSanityCheck)

const _modelValue = computed({
  get: () => modelValue.value,
  set: async (v) => {
    const out = [...(v ?? [])]

    if (props.selectType === 'folder' && v) {
      const x = await pathMeta.getAsync(v.join('/'))
      await until(() => x?.loading).toBe(false)
      if (x?.exists && originalFileOrFolder.value) {
        out.push(originalFileOrFolder.value)
      }
      modelValue.value = out
    }
    else {
      pathIsFileFn(v ?? []).then((isFile) => {
        if ((!isFile || props.selectType === 'folder') && originalFileOrFolder.value) {
          out.push(originalFileOrFolder.value)
        }

        modelValue.value = out
      })
    }
  },
})

const _newPathDebounced = refDebounced(_newPath, 400)

watch(_newPathDebounced, () => {
  nextTick(() => {
    _modelValue.value = _newPath.value
    inputRef.value?.focus()
  })
})

// Workaround: No title in search bar
// watch(menu, () => {
//   if (menu.value && (search.value !== fullPath.value))
//     search.value = fullPath.value
// })

// watch(newPath, () => {
//   if (search.value !== fullPath.value)
//     search.value = fullPath.value
// })

function indexMeta(index: number): ReturnType<typeof pathMeta.get> {
  return pathMeta.get(allPaths.value[index])
}

function getChipIcon(index: number): string {
  if (indexMeta(index)?.loading ?? true) {
    return mdiProgressQuestion
  }
  if (!indexMeta(index)?.exists) {
    if ((props.allowNewFolders || index === _newPath.value.length - 1) && index !== 0) {
      if (index === _newPath.value.length - 1) {
        switch (props.action) {
          case 'move':
            return mdiRedo
          case 'copy':
            return mdiContentCopy
          case 'select':
            return mdiPlus
        }
      }
      return mdiPlus
    }
    return mdiFolderRemove
  }

  return index === 0 ? mdiServer : indexMeta(index)?.folder ? mdiFolder : mdiFile
}

function getChipColor(index: number): string {
  if (indexMeta(index)?.loading ?? true) {
    return 'warning'
  }
  if (!indexMeta(index)?.exists) {
    if ((props.allowNewFolders || index === _newPath.value.length - 1) && index !== 0)
      return 'success'
    return 'error'
  }

  return ''
}

function onKeydown(e: KeyboardEvent): void {
  if (e.key === 'a' && e.ctrlKey && props.allowTextView) {
    focusTextView(() => {
      textViewRef.value?.setSelectionRange(0, textViewRef.value?.value.length)
    })
  }

  // If arrow left and caret is at the beginning, enable textView
  if (e.key === 'ArrowLeft' && inputRef.value?.selectionStart === 0 && props.allowTextView) {
    focusTextView()
    e.preventDefault()
    e.stopPropagation()
  }

  // If arrow right and caret is at the end, add a new path
  if (e.key === 'ArrowRight' && inputRef.value?.selectionStart === inputRef.value?.value.length) {
    if (search.value) {
      // Modern problems require modern solutions 🧙‍♂️
      _newPath.value = [..._newPath.value, search.value]

      search.value = ''
      inputRef.value.value = ''
      inputRef.value.blur()
      nextTick(() => {
        inputRef.value.focus()
      })
      e.preventDefault()
      e.stopPropagation()
    }
  }

  if (e.key === 'Backspace' && !search.value && _newPath.value.length > 1 && inputRef.value?.selectionStart === 0) {
    if (_newPath.value.length > (props.minPathLen ?? 0)) {
      _newPath.value = _newPath.value.slice(0, -1)
      inputRef.value?.blur()
      nextTick(() => {
        inputRef.value?.focus()
      })
    }
    e.preventDefault()
    e.stopPropagation()
  }
}

const inputViewModel = computed({
  get: () => `/${_newPath.value.join('/')}`,
  set: (v) => {
    _newPath.value = v.split('/').filter(x => x)
  },
})

function onKeydownTextView(e: KeyboardEvent): void {
  // If right arrow and caret is at the end, disable textView
  if (e.key === 'ArrowRight' && textViewRef.value?.selectionStart === textViewRef.value?.value.length) {
    textView.value = false
    nextTick(() => inputRef.value?.focus())
  }

  if (e.key === 'Enter' || e.key === 'Tab' || e.key === 'Escape') {
    textView.value = false
    nextTick(() => inputRef.value?.focus())
    e.preventDefault()
    e.stopPropagation()
  }
}

function onChipClick(index: number): void {
  if (!props.allowTextView)
    return
  // Switch to textView and select the text of the index
  focusTextView(() => {
    const selectionStart = (`/${_newPath.value.slice(0, index).join('/')}`).length + (index === 0 ? 0 : 1)
    const selectionEnd = selectionStart + _newPath.value[index].length
    textViewRef.value?.setSelectionRange(selectionStart, selectionEnd)
  })
}

function focusTextView(callback?: () => void): void {
  textView.value = true
  nextTick(() => {
    textViewRef.value?.focus()
    callback?.()
  })
}

const _rules = computed(() => {
  // Check for valid root path
  if (_newPath.value.length === 0) {
    return [() => t('error.emptyPath')]
  }

  if (!pathMeta.get(allPaths.value[0])?.exists) {
    return [() => t('error.invalidStorage')]
  }

  if (!props.allowNewFolders) {
    const hasAnyNonExistingFolders = _newPath.value.slice(0, -1).some((v, i) => !pathMeta.get(allPaths.value[i])?.exists)
    if (hasAnyNonExistingFolders) {
      return [() => t('error.missingFolders')]
    }
  }

  // If original path is same
  if (!props.allowSamePath && props.original && _newPath.value.join('/') === props.original.join('/')) {
    return [() => t('error.samePath')]
  }

  if (props.minPathLen && _newPath.value.length < props.minPathLen) {
    return [() => t('error.pathLessMin')]
  }

  return props.rules ?? []
})

function goto(path: string[]): void {
  __newPath.value = [...path]
}

defineExpose({ goto })
</script>

<template>
  <v-text-field v-if="textView" ref="textViewRef" v-model="inputViewModel" :label="label" variant="outlined" class="text-view-path" :rules="_rules" @blur="textView = false" @keydown="onKeydownTextView" />
  <v-combobox
    v-else
    ref="comboboxRef"
    v-model="__newPath"
    v-model:search="search"
    v-model:menu="menuRef"
    class="path-autocomplete"
    multiple
    chips
    no-filter
    item-title="title"
    item-value="value"
    :items="loading ? displayItemsExtra : displayItems"
    :loading="loading"
    variant="outlined"
    :hide-no-data="true"
    :rules="_rules"
    :label="label" :hide-details="hideDetails" @focus="hasFocus = true"
    @blur="hasFocus = false"
    @keydown="onKeydown"
    @dblclick="() => { if (props.allowTextView) focusTextView() }"
  >
    <template v-if="loading" #no-data />
    <template #chip="{ item, index }">
      <v-chip
        variant="text" rounded="xs"
        :color="getChipColor(index)"
        @click="onChipClick(index)"
      >
        <v-icon size="12" :icon="getChipIcon(index)" class="mr-1" /> {{ item.title }}
      </v-chip>
      <v-icon v-if="index < _newPath.length - 1" :icon="mdiChevronRight" size="12" />
      <!-- <template v-if="index === _newPath.length - 1 && !hasFocus && !pathIsFile">
        <v-icon :icon="mdiChevronRight" size="12" />
        <v-chip variant="text" rounded="xs" class="text-disabled">
          <v-icon size="12" :icon="mdiFile" class="mr-1" /> {{ originalFileOrFolder }}
        </v-chip>
      </template> -->
    </template>
    <template #item="{ item, props: props2 }">
      <v-list-item v-if="(item.raw as any).type === 'addInsideFile'">
        <v-icon :icon="mdiExclamation" color="error" class="mr-2" /> <span color="text-error">{{ t('addInsideFile') }}</span>
      </v-list-item>

      <v-list-item v-else v-bind="props2" title="">
        <!-- Show add file with original name -->
        <template v-if="(item.raw as any).type === 'addFileOriginalName'">
          <v-icon :icon="addItemIcon" class="mr-2" /> <span class="text-disabled">{{ t(addItemKey) }}:</span> {{ item.title
          }} <span class="text-caption text-disabled">({{
            t('originalName') }})</span>
        </template>
        <!-- Show add file -->
        <template v-else-if="(item.raw as any).type === 'addFile'">
          <v-icon :icon="addItemIcon" class="mr-2" /> <span class="text-disabled">{{ t(addItemKey) }}:</span> {{ item.title
          }}
        </template>
        <template v-else>
          <v-icon v-if="item.raw.pathType !== StorageNodeTypeEnum.File" :icon="mdiFolder" class="mr-2" /> {{ item.title }}
        </template>
      </v-list-item>
    </template>
  </v-combobox>
</template>

<style scoped>
.path-autocomplete:deep(.v-autocomplete__selection-text) {
  direction: rtl;
}

.text-view-path:deep(input) {
  font-family: monospace;
}
</style>

<i18n lang="yaml">
de:
  originalName: Originalname
  addFile: Hinzufügen
  moveFile: Verschieben nach
  copyFile: Kopieren nach
  renameFile: Umbenennen zu
  addInsideFile: Eine tiefere Ebene ist nicht möglich, da es sich um eine Datei handelt.
  error:
    missingFolders: Ordner existieren nicht
    samePath: Der Pfad ist bereits der aktuelle Pfad
    emptyPath: Der Pfad darf nicht leer sein
    invalidStorage: Storage nicht erreichbar
    pathLessMin: Der Pfad ist zu weit hoch

en:
  originalName: Original Name
  addFile: Add
  moveFile: Move
  copyFile: Copy to
  renameFile: Rename to
  addInsideFile: Adding a deeper level is not possible as it is a file.
  error:
    missingFolders: Folders do not exist
    samePath: The path is already the current path
    emptyPath: Path must not be empty
    invalidStorage: Storage not reachable
    pathLessMin: Path is too far up
</i18n>
